import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, LogOut, RefreshCw, Wallet } from "lucide-react";
import {
  fetchDistributorBrowseDetails,
  fetchDistributorDashboard,
  fetchDistributorRechargeDetails,
  fetchDistributorRegisterDetails,
  type ApiError,
  type DistributorBrowseDetail,
  type DistributorDashboard,
  type DistributorDailySummary,
  type DistributorRechargeDetail,
  type DistributorRegisterDetail,
} from "@/api/member";
import { clearDistributorSession, ensureDistributorAccessToken } from "@/utils/distributorSession";

type DashboardTab = "summary" | "browse" | "register" | "recharge" | "commission";

function formatMoney(value?: number) {
  return `¥${((value || 0) / 100).toFixed(2)}`;
}

function formatDateTime(value?: string) {
  if (!value) {
    return "-";
  }
  return String(value).replace("T", " ").slice(0, 19);
}

function formatPercent(bp?: number) {
  return `${(((bp || 0) / 10000) * 100).toFixed(2)}%`;
}

function formatPayType(value?: string) {
  const mapping: Record<string, string> = {
    alipay_h5: "Alipay H5",
    alipay_qr: "Alipay QR",
    wechat_qr: "WeChat QR",
    wechat_h5: "WeChat H5",
  };
  return mapping[value || ""] || value || "-";
}

function formatRechargeStatus(value?: string) {
  const mapping: Record<string, string> = {
    created: "创建中",
    pending: "待支付",
    paid: "已支付",
    failed: "失败",
    closed: "已关闭",
    expired: "已过期",
  };
  return mapping[value || ""] || value || "-";
}

function buildShareLink(shareCode: string) {
  if (typeof window === "undefined") {
    return `https://mfantasy.net/?ref=${shareCode}`;
  }
  return `${window.location.origin}/?ref=${encodeURIComponent(shareCode)}`;
}

export default function DistributorDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DashboardTab>("summary");
  const [dashboard, setDashboard] = useState<DistributorDashboard | null>(null);
  const [browseDetails, setBrowseDetails] = useState<DistributorBrowseDetail[]>([]);
  const [registerDetails, setRegisterDetails] = useState<DistributorRegisterDetail[]>([]);
  const [rechargeDetails, setRechargeDetails] = useState<DistributorRechargeDetail[]>([]);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [registerTotal, setRegisterTotal] = useState(0);
  const [rechargeTotal, setRechargeTotal] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(1);
  const [detailPage, setDetailPage] = useState(1);
  const pageSize = 10;

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await ensureDistributorAccessToken();
      if (!token) {
        navigate("/distributor/login", { replace: true });
        return;
      }
      const data = await fetchDistributorDashboard(token, {
        startTime: startDate ? `${startDate} 00:00:00` : undefined,
        endTime: endDate ? `${endDate} 23:59:59` : undefined,
      });
      setDashboard(data);
      setPage(1);
    } catch (err) {
      setError((err as ApiError).message || "分销数据加载失败");
      if ((err as ApiError).status === 401) {
        clearDistributorSession();
        navigate("/distributor/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [endDate, navigate, startDate]);

  const loadDetails = useCallback(
    async (tab: Exclude<DashboardTab, "summary">, nextPage = detailPage) => {
      setDetailLoading(true);
      setError("");
      try {
        const token = await ensureDistributorAccessToken();
        if (!token) {
          navigate("/distributor/login", { replace: true });
          return;
        }
        const params = {
          startTime: startDate ? `${startDate} 00:00:00` : undefined,
          endTime: endDate ? `${endDate} 23:59:59` : undefined,
          pageNum: nextPage,
          pageSize,
        };
        if (tab === "browse") {
          const data = await fetchDistributorBrowseDetails(token, params);
          setBrowseDetails(data.list || []);
          setBrowseTotal(data.total || 0);
          return;
        }
        if (tab === "register") {
          const data = await fetchDistributorRegisterDetails(token, params);
          setRegisterDetails(data.list || []);
          setRegisterTotal(data.total || 0);
          return;
        }
        const data = await fetchDistributorRechargeDetails(token, params);
        setRechargeDetails(data.list || []);
        setRechargeTotal(data.total || 0);
      } catch (err) {
        setError((err as ApiError).message || "明细数据加载失败");
        if ((err as ApiError).status === 401) {
          clearDistributorSession();
          navigate("/distributor/login", { replace: true });
        }
      } finally {
        setDetailLoading(false);
      }
    },
    [detailPage, endDate, navigate, startDate],
  );

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (activeTab === "summary") {
      return;
    }
    void loadDetails(activeTab, detailPage);
  }, [activeTab, detailPage, loadDetails]);

  const dailySummaries = useMemo(() => dashboard?.dailySummaries || [], [dashboard?.dailySummaries]);
  const totalPages = Math.max(1, Math.ceil(dailySummaries.length / pageSize));
  const pagedData = useMemo<DistributorDailySummary[]>(() => {
    const offset = (page - 1) * pageSize;
    return dailySummaries.slice(offset, offset + pageSize);
  }, [dailySummaries, page]);

  const shareLink = dashboard?.profile.shareCode ? buildShareLink(dashboard.profile.shareCode) : "";
  const detailTotal =
    activeTab === "browse" ? browseTotal : activeTab === "register" ? registerTotal : rechargeTotal;
  const detailTotalPages = Math.max(1, Math.ceil(detailTotal / pageSize));

  const copyShareLink = async () => {
    if (!shareLink) {
      return;
    }
    await navigator.clipboard.writeText(shareLink).catch(() => {});
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const logout = () => {
    clearDistributorSession();
    navigate("/distributor/login", { replace: true });
  };

  const handleSearch = () => {
    if (activeTab === "summary") {
      void loadDashboard();
      return;
    }
    setDetailPage(1);
    void loadDetails(activeTab, 1);
  };

  const summary = dashboard?.currentSummary || {
    browseCount: 0,
    registerCount: 0,
    rechargeCount: 0,
    rechargeAmount: 0,
    commissionAmount: 0,
  };

  return (
    <div className="min-h-screen bg-dark-800 text-white">
      <div className="sticky top-0 z-30 border-b border-dark-300/60 bg-dark-800/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex h-9 w-9 items-center justify-center rounded-full text-white/55 hover:text-white">
              <ArrowLeft size={20} />
            </Link>
            <span className="font-bold">推广管理</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/distributor/withdraw")}
              className="inline-flex h-9 items-center gap-2 rounded-button border border-dark-300 bg-dark-500 px-3 text-sm hover:bg-dark-400"
            >
              <Wallet size={16} />
              提现
            </button>
            <button
              type="button"
              onClick={logout}
              className="inline-flex h-9 items-center gap-2 rounded-button border border-dark-300 bg-dark-500 px-3 text-sm text-white/60 hover:text-white"
            >
              <LogOut size={16} />
              退出
            </button>
          </div>
        </div>
      </div>

      <main className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-6 md:px-6">
        {error && <div className="rounded-card border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>}

        <section className="grid gap-3 md:grid-cols-2">
          <div className="rounded-card border border-dark-300 bg-dark-500 p-4">
            <p className="text-xs text-white/40">一级分成比例</p>
            <p className="mt-2 text-2xl font-black text-gold-400">{formatPercent(dashboard?.profile.firstLevelRatioBp)}</p>
          </div>
          <div className="rounded-card border border-dark-300 bg-dark-500 p-4">
            <p className="text-xs text-white/40">二级分成比例</p>
            <p className="mt-2 text-2xl font-black text-white">{formatPercent(dashboard?.profile.secondLevelRatioBp)}</p>
          </div>
        </section>

        <section className="rounded-2xl border border-dark-300 bg-dark-500 p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs text-white/45">我的分享码</p>
              <p className="mt-1 text-3xl font-black tracking-widest text-gold-400">{dashboard?.profile.shareCode || "-"}</p>
              <p className="mt-2 max-w-full break-all text-xs text-white/35">{shareLink || "登录后生成推广链接"}</p>
            </div>
            <button
              type="button"
              onClick={() => void copyShareLink()}
              disabled={!shareLink}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-button bg-[#F97316] px-5 font-bold text-dark-900 hover:bg-[#F97316]/90 disabled:cursor-not-allowed disabled:opacity-55"
            >
              <Copy size={18} />
              {copied ? "已复制" : "复制推广链接"}
            </button>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          {[
            ["浏览次数", summary.browseCount.toLocaleString()],
            ["注册人数", summary.registerCount.toLocaleString()],
            ["充值笔数", summary.rechargeCount.toLocaleString()],
            ["充值金额", formatMoney(summary.rechargeAmount)],
            ["佣金收入", formatMoney(summary.commissionAmount)],
          ].map(([label, value]) => (
            <div key={label} className="rounded-card border border-dark-300 bg-dark-500 p-4">
              <p className="text-xs text-white/40">{label}</p>
              <p className="mt-2 text-xl font-black text-white">{value}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-3 md:grid-cols-3">
          <div className="rounded-card border border-mint-400/25 bg-dark-500 p-4">
            <p className="text-xs text-white/40">可提现余额</p>
            <p className="mt-2 text-2xl font-black text-mint-400">{formatMoney(dashboard?.profile.availableBalance)}</p>
          </div>
          <div className="rounded-card border border-dark-300 bg-dark-500 p-4">
            <p className="text-xs text-white/40">待处理提现</p>
            <p className="mt-2 text-2xl font-black text-gold-400">{formatMoney(dashboard?.profile.pendingWithdrawAmount)}</p>
          </div>
          <div className="rounded-card border border-dark-300 bg-dark-500 p-4">
            <p className="text-xs text-white/40">累计已提现</p>
            <p className="mt-2 text-2xl font-black text-white">{formatMoney(dashboard?.profile.withdrawnAmount)}</p>
          </div>
        </section>

        <section className="rounded-2xl border border-dark-300 bg-dark-500 p-5">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-bold">分销数据</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  ["summary", "每日汇总"],
                  ["browse", "浏览明细"],
                  ["register", "注册明细"],
                  ["recharge", "充值明细"],
                  ["commission", "分成流水"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setActiveTab(key as DashboardTab);
                      setDetailPage(1);
                    }}
                    className={`h-9 rounded-button px-4 text-sm font-bold ${
                      activeTab === key
                        ? "bg-[#F97316] text-dark-900"
                        : "border border-dark-300 bg-dark-700 text-white/60 hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="h-10 rounded-button border border-dark-300 bg-dark-700 px-3 text-sm outline-none focus:border-mint-400"
              />
              <span className="text-sm text-white/30">至</span>
              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="h-10 rounded-button border border-dark-300 bg-dark-700 px-3 text-sm outline-none focus:border-mint-400"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="inline-flex h-10 items-center gap-2 rounded-button bg-dark-400 px-3 text-sm hover:bg-dark-300"
              >
                <RefreshCw size={16} />
                查询
              </button>
            </div>
          </div>

          {activeTab === "summary" && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className="border-b border-dark-300 text-xs text-white/45">
                      <th className="px-3 py-3 text-left">日期</th>
                      <th className="px-3 py-3 text-right">浏览</th>
                      <th className="px-3 py-3 text-right">注册</th>
                      <th className="px-3 py-3 text-right">充值笔数</th>
                      <th className="px-3 py-3 text-right">充值金额</th>
                      <th className="px-3 py-3 text-right">佣金</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedData.map((item) => (
                      <tr key={item.date} className="border-b border-dark-300/50">
                        <td className="px-3 py-3">{item.date}</td>
                        <td className="px-3 py-3 text-right text-white/70">{item.browseCount}</td>
                        <td className="px-3 py-3 text-right text-white/70">{item.registerCount}</td>
                        <td className="px-3 py-3 text-right text-white/70">{item.rechargeCount}</td>
                        <td className="px-3 py-3 text-right text-gold-400">{formatMoney(item.rechargeAmount)}</td>
                        <td className="px-3 py-3 text-right font-bold text-mint-400">{formatMoney(item.commissionAmount)}</td>
                      </tr>
                    ))}
                    {!loading && pagedData.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-white/35">
                          暂无分销数据
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center justify-center gap-3">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                  className="h-9 rounded-button border border-dark-300 px-4 text-sm disabled:opacity-40"
                >
                  上一页
                </button>
                <span className="text-xs text-white/45">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                  className="h-9 rounded-button border border-dark-300 px-4 text-sm disabled:opacity-40"
                >
                  下一页
                </button>
              </div>
            </>
          )}

          {activeTab === "browse" && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1160px] text-sm">
                <thead>
                  <tr className="border-b border-dark-300 text-xs text-white/45">
                    <th className="px-3 py-3 text-left">时间</th>
                    <th className="px-3 py-3 text-left">剧集</th>
                    <th className="px-3 py-3 text-left">渠道</th>
                    <th className="px-3 py-3 text-left">来源页</th>
                    <th className="px-3 py-3 text-left">地区</th>
                    <th className="px-3 py-3 text-left">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {browseDetails.map((item) => (
                    <tr key={item.id} className="border-b border-dark-300/50">
                      <td className="px-3 py-3 text-white/70">{formatDateTime(item.createdAt)}</td>
                      <td className="px-3 py-3">{item.dramaTitle || `#${item.dramaId || "-"}`}</td>
                      <td className="px-3 py-3 text-white/60">{item.channel || "-"}</td>
                      <td className="max-w-[320px] truncate px-3 py-3 text-xs text-white/60" title={item.sourcePage || ""}>
                        {item.sourcePage || "-"}
                      </td>
                      <td className="px-3 py-3 text-white/60">{item.region || "-"}</td>
                      <td className="px-3 py-3 font-mono text-xs text-white/60">{item.ipAddress || "-"}</td>
                    </tr>
                  ))}
                  {!detailLoading && browseDetails.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-white/35">
                        暂无浏览明细
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "register" && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead>
                  <tr className="border-b border-dark-300 text-xs text-white/45">
                    <th className="px-3 py-3 text-left">注册时间</th>
                    <th className="px-3 py-3 text-left">用户</th>
                    <th className="px-3 py-3 text-left">邮箱</th>
                    <th className="px-3 py-3 text-left">分享码</th>
                    <th className="px-3 py-3 text-left">奖励状态</th>
                  </tr>
                </thead>
                <tbody>
                  {registerDetails.map((item) => (
                    <tr key={item.id} className="border-b border-dark-300/50">
                      <td className="px-3 py-3 text-white/70">{formatDateTime(item.createdAt)}</td>
                      <td className="px-3 py-3">{item.inviteeNickName || item.inviteeUserName || `#${item.inviteeUserId}`}</td>
                      <td className="px-3 py-3 text-white/60">{item.inviteeEmail || "-"}</td>
                      <td className="px-3 py-3 font-mono text-gold-400">{item.shareCode}</td>
                      <td className="px-3 py-3 text-white/60">{item.rewardGranted ? "已发放" : "未发放"}</td>
                    </tr>
                  ))}
                  {!detailLoading && registerDetails.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-white/35">
                        暂无注册明细
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "recharge" && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1120px] text-sm">
                <thead>
                  <tr className="border-b border-dark-300 text-xs text-white/45">
                    <th className="px-3 py-3 text-left">充值时间</th>
                    <th className="px-3 py-3 text-left">充值用户</th>
                    <th className="px-3 py-3 text-left">邮箱</th>
                    <th className="px-3 py-3 text-left">订单号</th>
                    <th className="px-3 py-3 text-center">层级</th>
                    <th className="px-3 py-3 text-right">充值金额</th>
                    <th className="px-3 py-3 text-right">佣金</th>
                    <th className="px-3 py-3 text-center">比例</th>
                    <th className="px-3 py-3 text-center">支付方式</th>
                    <th className="px-3 py-3 text-center">状态</th>
                  </tr>
                </thead>
                <tbody>
                  {rechargeDetails.map((item) => (
                    <tr key={item.id} className="border-b border-dark-300/50">
                      <td className="px-3 py-3 text-white/70">{formatDateTime(item.paidAt || item.createdAt)}</td>
                      <td className="px-3 py-3">{item.inviteeNickName || item.inviteeUserName || `#${item.inviteeUserId}`}</td>
                      <td className="px-3 py-3 text-white/60">{item.inviteeEmail || "-"}</td>
                      <td className="px-3 py-3 font-mono text-xs text-white/60">{item.orderNo || `#${item.orderId}`}</td>
                      <td className="px-3 py-3 text-center text-white/60">{item.level || 1}</td>
                      <td className="px-3 py-3 text-right text-gold-400">{formatMoney(item.rechargeAmount)}</td>
                      <td className="px-3 py-3 text-right font-bold text-mint-400">{formatMoney(item.commissionAmount)}</td>
                      <td className="px-3 py-3 text-center text-white/60">{formatPercent(item.ratioBasisPoint)}</td>
                      <td className="px-3 py-3 text-center text-white/60">{formatPayType(item.payType)}</td>
                      <td className="px-3 py-3 text-center text-white/60">{formatRechargeStatus(item.status)}</td>
                    </tr>
                  ))}
                  {!detailLoading && rechargeDetails.length === 0 && (
                    <tr>
                      <td colSpan={10} className="py-12 text-center text-white/35">
                        暂无充值明细
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "commission" && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b border-dark-300 text-xs text-white/45">
                    <th className="px-3 py-3 text-left">分成时间</th>
                    <th className="px-3 py-3 text-left">充值用户</th>
                    <th className="px-3 py-3 text-left">订单号</th>
                    <th className="px-3 py-3 text-center">层级</th>
                    <th className="px-3 py-3 text-center">分成比例</th>
                    <th className="px-3 py-3 text-right">充值金额</th>
                    <th className="px-3 py-3 text-right">分成金额</th>
                  </tr>
                </thead>
                <tbody>
                  {rechargeDetails.map((item) => (
                    <tr key={item.id} className="border-b border-dark-300/50">
                      <td className="px-3 py-3 text-white/70">{formatDateTime(item.createdAt)}</td>
                      <td className="px-3 py-3">{item.inviteeNickName || item.inviteeUserName || `#${item.inviteeUserId}`}</td>
                      <td className="px-3 py-3 font-mono text-xs text-white/60">{item.orderNo || `#${item.orderId}`}</td>
                      <td className="px-3 py-3 text-center text-white/60">{item.level || 1}</td>
                      <td className="px-3 py-3 text-center text-white/60">{formatPercent(item.ratioBasisPoint)}</td>
                      <td className="px-3 py-3 text-right text-gold-400">{formatMoney(item.rechargeAmount)}</td>
                      <td className="px-3 py-3 text-right font-bold text-mint-400">{formatMoney(item.commissionAmount)}</td>
                    </tr>
                  ))}
                  {!detailLoading && rechargeDetails.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-white/35">
                        暂无分成流水
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab !== "summary" && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={detailPage <= 1}
                onClick={() => setDetailPage((value) => Math.max(1, value - 1))}
                className="h-9 rounded-button border border-dark-300 px-4 text-sm disabled:opacity-40"
              >
                上一页
              </button>
              <span className="text-xs text-white/45">
                {detailPage} / {detailTotalPages}
              </span>
              <button
                type="button"
                disabled={detailPage >= detailTotalPages}
                onClick={() => setDetailPage((value) => Math.min(detailTotalPages, value + 1))}
                className="h-9 rounded-button border border-dark-300 px-4 text-sm disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
